/**
 * Single source of truth for everything the public site says.
 * Keep it factual: no invented metrics, no client logos, no testimonials.
 */

export const COMPANY = 'JK Automations';
export const EMAIL = 'hello@jk-automations.com';
export const PHONE_DISPLAY = '+1 (213) 297-7730';
export const PHONE_HREF = '+12132977730';
export const CITIES = 'Los Angeles and San Diego';

export const UGC_URL = 'https://ugc.jk-automations.com';
export const ARCEUS_URL = 'https://arceus.jk-automations.com';

export const POSITIONING =
  'JK Automations helps local businesses get more customers and remove work that slows them down. We can run marketing end to end or improve the part your team needs, using software, automation and AI where each fits.';

export const HEADLINE = 'More customers. Not more dashboards.';
export const SUBHEAD =
  'We can run your marketing end to end or work alongside your team on the part that needs help. We make the creative, track leads, and show what brings in real inquiries and booked work.';

/** The rest of the company, named without competing with the headline. */
export const HERO_NOTE =
  'Need something else fixed? We build custom software and automations around the tools and team you already have.';

/* ---- What you get: the homepage offer grid ---- */

export interface OfferItem {
  name: string;
  body: string;
}

export const OFFER: readonly OfferItem[] = [
  {
    name: 'Ads that bring customers',
    body:
      'Instagram, Facebook and Google. We write it, shoot it, run it and cut what does not work. You own the ad accounts; we manage them.',
  },
  {
    name: 'Every call answered, every lead counted',
    body:
      'Calls get answered by an AI receptionist set up for your business, and every lead lands in one list with where it came from. Missed-call text-back and booking straight into your calendar are coming soon.',
  },
  {
    name: 'A report you can read in a minute',
    body:
      'Every week: what we spent, what came in, what it cost per lead, and what we are doing next. Sent by email, with the full report in your Arceus workspace. Report by text is coming soon.',
  },
];

/** The quiet fourth line under the offer grid - the only other mention of automations besides how-it-works step 1. */
export const OFFER_FOOTNOTE =
  'Need help with calls, custom software or one workflow? We scope that around your business instead of selling you a fixed package.';

/* ---- The department demos ---- */

export interface ConsoleRow {
  label: string;
  value: string;
}

export interface TranscriptTurn {
  who: string;
  text: string;
}

export interface ConsoleLine {
  who: string;
  role: 'owner' | 'system';
  text: string;
  rows?: readonly ConsoleRow[];
  transcript?: readonly TranscriptTurn[];
  approve?: boolean;
}

export interface Department {
  id: string;
  name: string;
  /** Honest status - never better than the gate the capability has passed. */
  status: 'Live' | 'Ready to start' | 'Needs setup';
  owns: string;
  /** What it does, in words anyone reads once. */
  plain: string;
  /** The seats in this department, Arceus first. */
  people?: readonly string[];
  sources: readonly string[];
  script: readonly ConsoleLine[];
}

export const DEPARTMENTS: readonly Department[] = [
  {
    id: 'marketing',
    name: 'Marketing',
    status: 'Live',
    owns: 'Campaigns, ad spend, copy, and what it actually booked.',
    plain:
      'Plans your campaigns, makes the creative, and tells you which ads brought in customers: not clicks, customers. Our team sets the ads up with you; Arceus reads Google Ads and Instagram numbers (read-only today).',
    sources: ['Google Ads', 'Instagram', 'Calendar'],
    script: [
      {
        who: 'Owner',
        role: 'owner',
        text: 'Where did last month’s ad spend actually go?',
      },
      {
        who: 'Arceus',
        role: 'system',
        text: '$4,120 across nine ad sets. Two of them booked almost everything.',
        rows: [
          { label: 'Meta · intro-offer retargeting', value: '$1,180 → 14 consults' },
          { label: 'Google · “near me” search', value: '$960 → 9 consults' },
          { label: 'The other seven ad sets', value: '$1,980 → 1 consult' },
        ],
      },
      {
        who: 'Arceus',
        role: 'system',
        text: 'I would pause the seven and move that $1,980 onto the two that book. Prepare it?',
        approve: true,
      },
    ],
  },
  {
    id: 'front-desk',
    name: 'Front desk',
    status: 'Ready to start',
    owns: 'Answers the phone, logs every caller, escalates what it cannot handle.',
    plain:
      'Answers your calls and counts every one as a lead with its source. Texting back missed calls and booking straight into your calendar are coming soon.',
    sources: ['Retell voice', 'Calendar (read-only)'],
    script: [
      {
        who: 'Owner',
        role: 'owner',
        text: 'Did anyone pick up the calls we missed Saturday?',
      },
      {
        who: 'Arceus',
        role: 'system',
        text: 'Six came in between 9am and 2pm. I answered all six: here is the third one.',
        transcript: [
          { who: 'Caller', text: 'Hi, do you do lip filler on weekends?' },
          {
            who: 'Arceus',
            text: 'We do, Saturdays until three. I have 11:30 or 1:15 this Saturday, which suits you?',
          },
          { who: 'Caller', text: '11:30 works.' },
          { who: 'Arceus', text: 'Booked. You will get a text confirmation in a moment.' },
        ],
      },
      {
        who: 'Arceus',
        role: 'system',
        text: 'Three booked, two were not a fit, one wants a callback Monday. Put it on your calendar?',
        approve: true,
      },
    ],
  },
  {
    id: 'operations',
    name: 'Back office',
    status: 'Needs setup',
    owns: 'The repeat work behind the desk, and a flag when a person is needed.',
    plain:
      'Coming soon: chases unpaid invoices, keeps the books current, and flags anything that needs your decision. Bank and email connections are not switched on yet, so this is set up per client.',
    sources: ['QuickBooks', 'Gmail', 'Plaid'],
    script: [
      {
        who: 'Owner',
        role: 'owner',
        text: 'What is still unpaid past thirty days?',
      },
      {
        who: 'Arceus',
        role: 'system',
        text: 'Four invoices, $11,240 together. Two have gone quiet for six weeks.',
        rows: [
          { label: 'Riverside Studio · inv 2841', value: '$4,800 · 44 days' },
          { label: 'Sunrise Detailing · inv 2855', value: '$3,150 · 41 days' },
          { label: 'Two more under $2,000', value: '$3,290 · 31–34 days' },
        ],
      },
      {
        who: 'Arceus',
        role: 'system',
        text: 'I drafted a reminder for each, softer on the two that always pay late. Send them?',
        approve: true,
      },
    ],
  },
];

/** Custom work, named beside the departments so the firm side is visible. */
export const CUSTOM_BUILD = {
  name: 'Custom',
  status: 'We build it' as const,
  owns: 'When the employee you need does not exist yet, we write the software.',
  plain: 'A department for your business alone: scoped, built and run by us.',
};

/* ---- Who Arceus is ---- */

export const ARCEUS_ABOUT = {
  lead: 'Arceus runs the week. You approve it.',
  lines: [
    'Arceus guides the JK Business OS. It reads the accounts you approve, drafts the work, logs each step, and stops before anything sends or spends.',
    'Clients sign in at arceus.jk-automations.com to see the work, the decisions, and the results. Workspaces are set up by JK, by invitation.',
  ],
  controls: [
    { id: 'add', symbol: '+', name: 'Capture', means: 'Reads the calls, forms and ad numbers from the accounts you connect.' },
    { id: 'run', symbol: '▶', name: 'Run', means: 'Does the week’s work and logs every step.' },
    { id: 'approve', symbol: '✓', name: 'Approve', means: 'Stops and waits for you before anything sends or spends.' },
  ],
} as const;

/* ---- The software firm beside the hire ---- */

export interface Build {
  id: 'apps' | 'ads';
  name: string;
  plain: string;
  proof: string;
  link?: { href: string; label: string };
}

export const BUILDS: readonly Build[] = [
  {
    id: 'apps',
    name: 'Custom apps and sites',
    plain: 'Web and mobile, built to fit how your business already works, not the other way round.',
    proof: 'Ask Arceus for it. He logs the request and JK scopes it; you get a price in writing.',
  },
  {
    id: 'ads',
    name: 'Ad creative and creator content',
    plain: 'Short-form, creator-led and AI-assisted production when it fits the audience and campaign.',
    proof: 'Every frame here is from a different spot we produced in house.',
    link: { href: UGC_URL, label: 'See the ad and UGC work' },
  },
];

/* ---- How an engagement starts, and how it is priced ---- */

export const HOW_IT_STARTS = [
  {
    n: '1',
    name: 'Free audit',
    body: 'A 20-minute call about your goals, the work slowing you down and what your team already uses.',
  },
  {
    n: '2',
    name: 'The right scope, in writing',
    body: 'We recommend the part worth fixing first, with a clear price. Ad spend stays in your accounts.',
  },
  {
    n: '3',
    name: 'Build, run and measure',
    body: 'We launch the agreed work, keep people on important decisions and show you what changed.',
  },
] as const;

export const PRICING_NOTE = [
  'Every team is quoted, starting from a free audit.',
  'After that, we build a custom team around your business and send you the scope in writing.',
  'Departments run month to month.',
] as const;

export const INDUSTRIES_LEDE = 'Different businesses. Different work worth fixing.';

/** The verticals as cards: an icon, the name, and the question an owner there actually asks. */
export interface IndustryCard {
  name: string;
  icon: string;
  ask: string;
}

export const INDUSTRY_CARDS: readonly IndustryCard[] = [
  { name: 'Med spas', icon: 'medspa', ask: 'Which ad brought in the last ten consults?' },
  { name: 'HVAC', icon: 'hvac', ask: 'Why did the phone stop ringing in March?' },
  { name: 'Plumbing', icon: 'plumbing', ask: 'How many after-hours calls did we miss last week?' },
  { name: 'Auto repair', icon: 'auto', ask: 'What does a new customer cost us on Google?' },
  { name: 'Orthopedic practices', icon: 'ortho', ask: 'Which reviews are new patients actually reading?' },
  { name: 'PI & law', icon: 'law', ask: 'What did last month’s leads cost per signed case?' },
  { name: 'Gyms, MMA & BJJ', icon: 'gym', ask: 'Which post brought in the trial sign-ups?' },
  { name: 'Pilates & wellness', icon: 'pilates', ask: 'How many intro-offer people came from Instagram?' },
  { name: 'Bars & restaurants', icon: 'restaurant', ask: 'Did the weekend promo fill any tables?' },
  { name: 'Owner-operator trucking', icon: 'trucking', ask: 'Is the ad spend finding shippers or just clicks?' },
  { name: 'Creators', icon: 'creators', ask: 'Which posts actually drove DMs?' },
];

export interface Service {
  id: string;
  name: string;
  tag?: string;
  body: string;
  link?: { href: string; label: string };
}

export const SERVICES: readonly Service[] = [
  {
    id: 'consulting',
    name: 'AI consulting & builds',
    body: 'We map what your team does by hand, then build what replaces it.',
  },
  {
    id: 'ads',
    name: 'Ads & creative',
    body: 'Campaigns, creator content and AI-assisted production where it fits.',
    link: { href: UGC_URL, label: 'See the UGC service' },
  },
  {
    id: 'builds',
    name: 'Custom sites & apps',
    body: 'Web and mobile, live in weeks.',
  },
  {
    id: 'arceus',
    name: 'Arceus',
    tag: 'Early access',
    body: 'Our own AI operations employee.',
    link: { href: '#arceus', label: 'Read about Arceus' },
  },
];

export interface Step {
  id: string;
  name: string;
  body: string;
  output: string;
}

export const HOW_WE_WORK: readonly Step[] = [
  {
    id: 'audit',
    name: 'Audit',
    body: 'We map the manual work',
    output: 'a written map, fixed scope and price.',
  },
  {
    id: 'build',
    name: 'Build',
    body: 'We build it ourselves',
    output: 'a system on your accounts.',
  },
  {
    id: 'run',
    name: 'Run',
    body: 'We stay on the account',
    output: 'monitoring and a person who answers.',
  },
];

export const APPROVAL_NOTE = 'You approve anything that goes out.';

/** The eight questions on the Arceus strip - one per vertical, all concrete. */
export interface Ask {
  who: string;
  q: string;
}

export const ASKS: readonly Ask[] = [
  { who: 'Med spa', q: 'Which Botox clients are due to rebook?' },
  { who: 'HVAC', q: 'Which maintenance agreements renew in October?' },
  { who: 'Restaurant', q: 'What did we 86 last week?' },
  { who: 'Auto repair', q: 'Which estimates over $800 went unapproved?' },
  { who: 'Gym', q: 'Who has been frozen past 60 days?' },
  { who: 'Trucking', q: 'Does this load clear $2 a mile?' },
  { who: 'Law', q: 'Which retainers dropped under $500?' },
  { who: 'Creators', q: 'Which posts actually drove DMs?' },
];

/**
 * The connections wall. Row A is what the opsManager codebase supports today -
 * every entry traces to a provider slug in that repo. Row B is the Composio
 * catalogue we can wire on request; it is labelled as such, not claimed.
 * `mark` is a simple-icons slug in /brand/apps; null renders a text pill.
 */
export interface AppMark {
  name: string;
  mark: string | null;
}

/** Live in production today. Instagram, Analytics and Calendar are read-only. Source: Arceus connections catalog, 2026-10-30 (Google Ads is switched off there, so it is not listed). */
export const CONNECTED_TODAY: readonly AppMark[] = [
  { name: 'Instagram', mark: 'instagram' },
  { name: 'Google Analytics', mark: 'googleanalytics' },
  { name: 'Google Calendar', mark: 'googlecalendar' },
  { name: 'Retell voice', mark: null },
  { name: 'CSV import', mark: null },
];

/** Built or planned but switched off today. Shown dashed, never counted as connected. */
export const CONNECT_COMING_SOON: readonly AppMark[] = [
  { name: 'Google Ads', mark: 'googleads' },
  { name: 'Gmail', mark: 'gmail' },
  { name: 'Google Business Profile', mark: 'google' },
  { name: 'Search Console', mark: 'googlesearchconsole' },
  { name: 'Meta Ads', mark: 'meta' },
  { name: 'SignalWire SMS', mark: 'signalwire' },
  { name: 'Yelp', mark: 'yelp' },
];

export const CONNECT_ON_REQUEST: readonly AppMark[] = [
  { name: 'Square', mark: 'square' },
  { name: 'Shopify', mark: 'shopify' },
  { name: 'Toast', mark: 'toast' },
  { name: 'Clover', mark: 'clover' },
  { name: 'Stripe', mark: 'stripe' },
  { name: 'QuickBooks', mark: 'quickbooks' },
  { name: 'Mindbody', mark: 'mindbody' },
  { name: 'Vagaro', mark: 'vagaro' },
  { name: 'Calendly', mark: 'calendly' },
  { name: 'Slack', mark: 'slack' },
  { name: 'Notion', mark: 'notion' },
  { name: 'Airtable', mark: 'airtable' },
  { name: 'ServiceTitan', mark: 'servicetitan' },
  { name: 'Jobber', mark: 'jobber' },
  { name: 'Housecall Pro', mark: 'housecallpro' },
];

/** The audit-map window on the consulting row. */
export const AUDIT_MAP: readonly { task: string; verdict: 'automate' | 'human' | 'later' }[] = [
  { task: 'Chasing unapproved estimates', verdict: 'automate' },
  { task: 'Weekly review replies', verdict: 'automate' },
  { task: 'Rebooking reminders', verdict: 'automate' },
  { task: 'Quoting a custom job', verdict: 'human' },
  { task: 'Firing a customer', verdict: 'human' },
  { task: 'Inventory reorder points', verdict: 'later' },
];

export interface WorkItem {
  id: string;
  name: string;
  note: string;
  /** the short caption used on the home page windows */
  short?: string;
  status: string;
  inProgress?: boolean;
  href?: string;
  host?: string;
  image?: string;
  stills?: readonly string[];
}

export const WORK: readonly WorkItem[] = [
  {
    id: 'ironload',
    name: 'IronLoad',
    note: 'Tells an owner-operator whether a load pays before they accept it.',
    short: 'Load math before you accept',
    status: 'Live · product',
    href: 'https://ironload.jk-automations.com',
    host: 'ironload.jk-automations.com',
    image: '/shots/ironload-1280.png',
  },
  {
    id: 'peppy',
    name: 'Peppy',
    note: 'An AI fitness coach that keeps training, sleep and recovery in one place.',
    short: 'AI fitness coach',
    status: 'Live · product',
    href: 'https://peppy.jk-automations.com',
    host: 'peppy.jk-automations.com',
    image: '/shots/peppy-1280.jpg',
  },
  {
    id: 'trickbracket',
    name: 'TrickBracket',
    note: 'Runs a tricking tournament from one phone: check-in, seeding, live brackets, public results.',
    short: 'Tournament ops from a phone',
    status: 'Live · product',
    href: 'https://trickbracket.vercel.app',
    host: 'trickbracket.vercel.app',
    image: '/shots/trickbracket-1280.jpg',
  },
  {
    id: 'ugc',
    name: 'Ad creative & creator content',
    note: 'Creator-led and AI-generated spots, produced in house. We choose the format to fit the audience.',
    short: 'Ad creative and creator content',
    status: 'Live · service',
    href: UGC_URL,
    host: 'ugc.jk-automations.com',
    image: '/portfolio/ugc.jpg',
  },
  {
    id: 'arceus',
    name: 'JK Business OS',
    note: 'A client workspace guided by Arceus: talk through priorities, review work and approvals, and see marketing progress in one place. Early access, set up by JK.',
    short: 'Guided by Arceus',
    status: 'Early access · client workspace',
    href: ARCEUS_URL,
    host: 'arceus.jk-automations.com',
    image: '/shots/arceus-landing-live-20260925.png',
  },
  {
    id: 'showcase',
    name: 'Ad production showcase',
    note: 'Frames from spots we produce in house: storyboard, keyframes, motion and grade.',
    short: 'In-house ad pipeline',
    status: 'In production · showcase',
    inProgress: true,
    host: 'in-house pipeline',
    stills: ['/portfolio/city.jpg', '/portfolio/colossus.jpg'],
  },
];

/** The "what else we build" strip on the home page. */
export const PRODUCT_TILE_IDS: readonly string[] = ['ironload', 'peppy', 'trickbracket', 'ugc'];

/** Said plainly above the products, so nobody mistakes them for the offer. */
export const PRODUCTS_NOTE =
  'These examples show products and creative we build and maintain. They show our range, not a fixed package you have to buy.';

/* The ad reel moved to ./ads (one curated library, filtered for Home and /marketing). */

/** Kept for the Work page. */
export const STORYBOARD: readonly { src: string; label: string }[] = [
  { src: '/portfolio/city.jpg', label: '01' },
  { src: '/portfolio/city-2.jpg', label: '02' },
  { src: '/portfolio/colossus.jpg', label: '03' },
  { src: '/portfolio/colossus-2.jpg', label: '04' },
];

export const INDUSTRIES: readonly string[] = [
  'Med spas',
  'HVAC',
  'Plumbing',
  'Auto repair',
  'Orthopedic practices',
  'PI & law',
  'Gyms, MMA & BJJ',
  'Pilates & wellness',
  'Bars & restaurants',
  'Owner-operator trucking',
  'Creators',
];

/** The lead-ticker strip under the hero - fictional, generic events, no client names. */
export const LEAD_TICKER: readonly string[] = [
  'New lead · Instagram · 2m ago',
  'Call answered · 11:40pm · booked Thu 2:15',
  'Missed call · reply drafted for your OK',
  'Report sent · Fri 7:58am',
  'New lead · Google search · 14m ago',
  'Call answered · booked Sat 10:00',
];

export const NOW_BUILDING: readonly string[] = [
  'Arceus departments (marketing, finance and reviews), each gated behind owner approval.',
  'Private AI inference on our own Mac fleet, to keep more of a client’s data on hardware we control.',
  'Texting: missed-call text-back and report replies by text. Built, waiting on US carrier approval.',
  'Letting Arceus launch and adjust Google and Meta ads. Built and tested, switched off until it is proven safe. Today our team sets ads up by hand with your OK.',
  'Reading Gmail, WhatsApp, Google Business Profile and Search Console. Not switched on yet.',
  'A video pipeline that combines 3D scenes in Blender with generative video.',
  'An evaluation lab we use to grade our own AI employees before a client meets one.',
];

export const ABOUT_SHORT: readonly string[] = [
  'We do the audit, write the code, and stay on the account after launch.',
  'Everything we sell runs in one of our own products first.',
];

export const ABOUT: readonly string[] = [
  'JK Automations is run by its two founders. We do the audit, write the code, and stay on the account after launch; there is no layer between you and the people building your system.',
  'Everything we sell runs in one of our own businesses or products first. That is why the work we show is ours rather than a wall of client logos: we would rather show you something we still maintain.',
  `We work with small and service businesses across ${CITIES}.`,
];

export interface Founder {
  slug: string;
  name: string;
  role: string;
}

export const FOUNDERS: readonly Founder[] = [
  { slug: 'keanu', name: 'Keanu Santos-Frost', role: 'Co-founder & CEO' },
  { slug: 'justin', name: 'Justin Gamm', role: 'Co-founder & CTO' },
];

/* ---- Arceus over text ---- */

export interface TextMessage {
  who: 'arceus' | 'owner';
  text: string;
  chips?: readonly string[];
  time?: string;
}

export const TEXT_THREAD: readonly TextMessage[] = [
  {
    who: 'arceus',
    text: 'Morning. Week 38 report: 23 leads, 31 calls answered, $640 spent, $28 a lead. The Instagram intro-offer ad did most of it.',
    time: '7:58 AM',
  },
  {
    who: 'arceus',
    text: 'I want to move $200 from the Google ad to the Instagram one this week. OK?',
    chips: ['Yes', 'Hold'],
  },
  { who: 'owner', text: 'yes' },
  {
    who: 'arceus',
    text: 'Done. Next report Friday.',
  },
];

/* ---- Custom software, as Arceus explains it ---- */

export const CUSTOM_SCRIPT: readonly ConsoleLine[] = [
  {
    who: 'Owner',
    role: 'owner',
    text: 'Can you text each customer a photo of the technician before they arrive?',
  },
  {
    who: 'Arceus',
    role: 'system',
    text: 'Not something I run yet. I have alerted JK; they build custom software for exactly this.',
    rows: [
      { label: 'Request logged', value: '#0421 · custom build' },
      { label: 'Routed to', value: 'JK: Keanu and Justin' },
      { label: 'You will get', value: 'A written scope and price' },
    ],
  },
  {
    who: 'Arceus',
    role: 'system',
    text: 'Expect it within two days. Once it is built, it becomes something I run for you.',
  },
];

/* ---- The marketing page ---- */

/** A second worked example for the marketing page, so it does not repeat the homepage. */
export const MARKETING_EXAMPLE = {
  sources: ['Meta Ads', 'Mindbody', 'SignalWire SMS'],
  script: [
    { who: 'Owner', role: 'owner', text: 'Did the back-to-school trial offer actually do anything?' },
    {
      who: 'Arceus',
      role: 'system',
      text: 'Forty-one trial sign-ups from the Meta ad. Twenty-six showed up. Nine have joined so far.',
      rows: [
        { label: 'Trial sign-ups · Meta ad', value: '41 · $612 spent' },
        { label: 'Showed up for a class', value: '26' },
        { label: 'Joined as members', value: '9 → $1,341/mo' },
      ],
    },
    {
      who: 'Arceus',
      role: 'system',
      text: 'Scribe drafted a text for the fifteen who never came in: a second free class this Saturday. Send it?',
      approve: true,
    },
  ] as readonly ConsoleLine[],
} as const;

export const MARKETING_HEADLINE = 'Ads that bring in customers, not clicks.';
export const MARKETING_SUBHEAD =
  'We can manage Instagram, Facebook and Google end to end or support your team with the pieces it needs. We make the creative in house and report the inquiries and bookings we can verify.';

export interface Teammate {
  id: string;
  name: string;
  role: string;
  /** Mark colour: Arceus is the halo gold; the seats take the medallion gems and the robe. */
  color: string;
  /** The seat's own render, from the product's marketing workforce set. */
  image?: string;
  /** What the render carries - the fastest way to tell the four apart. */
  carries?: string;
  status: 'Live' | 'Ready to start' | 'Runs with your approval' | 'Needs setup';
  does: string;
}

/**
 * The marketing department as it exists in the product today. Statuses come
 * from the department presentation in opsManager and never exceed them:
 * Arceus is the live director; Scout and Scribe are ready; Launch is gated
 * behind an explicit approval; Pulse needs connected ad accounts.
 *
 * Not rendered on the public site anymore (the pivot removed the org-chart
 * presentation of the four specialists) - kept exported for reuse.
 */
export const MARKETING_TEAM: readonly Teammate[] = [
  {
    id: 'arceus',
    color: '#d9a441',
    name: 'Arceus',
    role: 'Marketing director',
    status: 'Live',
    does: 'Turns your goals into a bounded campaign brief, and keeps every approval with you.',
  },
  {
    id: 'scout',
    carries: 'Map pin and a field tablet',
    image: '/team/scout.webp',
    color: '#2f5fd6',
    name: 'Scout',
    role: 'Market researcher',
    status: 'Ready to start',
    does: 'Collects local, audience and competitor findings, each with its source.',
  },
  {
    id: 'scribe',
    carries: 'Quill crest and a purple scarf',
    image: '/team/scribe.webp',
    color: '#0f7f86',
    name: 'Scribe',
    role: 'Copywriter',
    status: 'Ready to start',
    does: 'Writes grounded ad copy and short-form video scripts from facts you supplied.',
  },
  {
    id: 'launch',
    carries: 'Launch arrow and a locked case',
    image: '/team/launch.webp',
    color: '#e0902a',
    name: 'Launch',
    role: 'Campaign operator',
    status: 'Runs with your approval',
    does: 'Prepares channel-ready campaign packages, and only ships after you say so.',
  },
  {
    id: 'pulse',
    carries: 'Heartbeat antenna and a chart',
    image: '/team/pulse.webp',
    color: '#1f9a5a',
    name: 'Pulse',
    role: 'Performance analyst',
    status: 'Needs setup',
    does: 'Summarises verified results once your ad accounts are connected.',
  },
];

export const MARKETING_MONTH = [
  { week: 'Week 1', title: 'Audit and setup', body: 'We review your ad accounts, phone line and calendar (read-only to start) and find where the money leaks.' },
  { week: 'Week 2', title: 'First campaign, approved', body: 'We research, write, and shoot. You approve the ad and the budget.' },
  { week: 'Week 3', title: 'Live, with receipts', body: 'Ads go live with your approval, set up by our team. Spend and calls are counted from your own accounts.' },
  { week: 'Week 4', title: 'What actually booked', body: 'You get the report: leads, calls, cost per lead, and the next move.' },
] as const;

/* ---- Pivot: the weekly report example (hero visual) ---- */

export interface WeeklyStat {
  label: string;
  value: string;
  note?: string;
}

export interface WeeklySourceRow {
  label: string;
  value: string;
}

export interface WeeklyReportExample {
  host: string;
  week: string;
  client: string;
  stats: readonly WeeklyStat[];
  sourcesLabel: string;
  sources: readonly WeeklySourceRow[];
  nextLabel: string;
  next: string;
  approvedAt: string;
}

export const WEEKLY_REPORT_EXAMPLE: WeeklyReportExample = {
  host: 'report · week of sep 15',
  week: 'Week 38',
  client: 'Northside Pilates',
  stats: [
    { label: 'New leads', value: '23' },
    { label: 'Calls answered', value: '31', note: '6 after hours' },
    { label: 'Ad spend', value: '$640' },
    { label: 'Cost per lead', value: '$28' },
  ],
  sourcesLabel: 'Where they came from',
  sources: [
    { label: 'Instagram intro-offer ad', value: '14' },
    { label: 'Google “pilates near me”', value: '6' },
    { label: 'Walk-in / referral', value: '3' },
  ],
  nextLabel: 'Next week',
  next: 'Move $200 from the Google ad to the Instagram ad. You approve it in Arceus.',
  approvedAt: 'Fri 7:58am',
};

/* ---- Your team: Arceus, four marketing specialists, and Echo at the Front Desk ----
   Source of truth: the Arceus roster (opsManager lib/team/roster.ts and the
   public team section), checked 2026-10-30. Launch retired on 2026-10-28; its
   campaign work moved to Director. Scout joined as the researcher. */

export type DepartmentId = 'front-desk' | 'marketing' | 'digital-ops';

export interface TeamMember {
  id: string;
  name: string;
  /** Plain job title, so nobody has to guess what a made-up name does. */
  role: string;
  /** One client-facing line. Honest: where a person approves, it says so. No result numbers. */
  job: string;
  /** Three concrete things this teammate handles. */
  handles: readonly string[];
  /** Accent colour: tints the portrait backdrop and the role label. */
  accent: string;
  /** Image alt text. */
  alt: string;
  department: DepartmentId;
  /** Portrait path, or null when no approved portrait exists yet (renders a neutral tile). */
  art: string | null;
  /** Short status shown as a pill when the teammate is not yet working for clients. */
  status?: string;
  /** Who this teammate hands its work to. */
  reportsTo?: string;
}

export const TEAM_LEAD: TeamMember = {
  id: 'arceus',
  name: 'Arceus',
  role: 'The lead',
  job: 'Runs your week: plans the work, checks every number, asks for your OK, and sends the Friday report.',
  handles: ['Weekly plan', 'Your approvals', 'Friday report'],
  accent: '#d9a441',
  alt: 'Arceus, the lead of the team: a robed figure with a gold halo and three gems on the chest',
  department: 'digital-ops',
  art: '/team/v4/arceus.webp',
};

export const TEAM_SPECIALISTS: readonly TeamMember[] = [
  {
    id: 'lens',
    name: 'Lens',
    role: 'The analyst',
    job: 'Finds what is costing you customers: your website, Google listing, competitors and the numbers behind every lead. A JK person reviews each audit before you see it.',
    handles: ['Website and Google listing audit', 'Competitor check', 'Lead numbers, explained'],
    accent: '#2f5fd6',
    alt: 'Lens, the analyst: an ivory and gold figure with an astrolabe halo and a sapphire gem',
    department: 'marketing',
    art: '/team/v4/lens.webp',
  },
  {
    id: 'director',
    name: 'Director',
    role: 'The creative',
    job: 'Drafts your posts, captions, scripts, ad copy and campaign plans for your OK. JK\'s production team makes the images and videos.',
    handles: ['Posts and copy', 'Scripts and ad copy', 'Campaign plans'],
    accent: '#d64545',
    alt: 'Director, the creative: an ivory and gold figure with an aperture halo and a ruby gem',
    department: 'marketing',
    art: '/team/v4/director.webp',
  },
  {
    id: 'scout',
    name: 'Scout',
    role: 'The researcher',
    job: 'Researches your market and competitors with source-linked findings. Never presents a guess as a verified fact.',
    handles: ['Market and competitor research', 'Source-linked findings', 'Hands verified facts to Director'],
    accent: '#7c5cf0',
    alt: 'Scout, the researcher',
    department: 'marketing',
    art: '/team/v4/scout.webp',
  },
  {
    id: 'forge',
    name: 'Forge',
    role: 'The builder',
    job: 'Finds what to fix on your website, tracking and Google listing and prepares the changes for you to review first. JK reviews and applies the approved changes.',
    handles: ['Website fix proposals', 'Tracking and analytics', 'Google listing updates, for your OK'],
    accent: '#2f9e5f',
    alt: 'Forge, the builder: an ivory and gold figure with a gear halo and an emerald gem',
    department: 'marketing',
    art: '/team/v4/forge.webp',
  },
  {
    id: 'echo',
    name: 'Echo',
    role: 'The front desk',
    job: 'Makes sure no lead goes cold: reviews your calls and drafts replies and follow-ups for your OK. Delivery depends on your connected services and approvals.',
    handles: ['Call review', 'Drafted replies and follow-ups', 'Texting: waiting on carrier approval'],
    accent: '#0e9fb8',
    alt: 'Echo, the front desk: an ivory and gold figure with a ripple halo, a headset and a cyan gem',
    department: 'front-desk',
    art: '/team/v4/echo.webp',
  },
];

/**
 * The CRM desk: three helpers that work inside the Arceus CRM alongside Echo.
 * Source: opsManager lib/team/roster.ts (CRM_DEPARTMENT_AGENTS) and
 * agents/SPECIALIST-DESKS.json, 2026-10-30. All three are in JK internal
 * trials: every draft is reviewed by a person, nothing is sent to a customer,
 * and no booking is claimed without a real calendar confirmation.
 */
export const TEAM_CRM: readonly TeamMember[] = [
  {
    id: 'desk',
    name: 'Front Desk',
    role: 'The intake',
    job: 'Takes in every new lead, from a web form, a missed call, an import or a first text, creates the contact and drafts the first reply. Urgent or unsupported requests go to a person.',
    handles: ['New-lead intake', 'First-reply drafts', 'After-hours leads and call cards'],
    accent: '#5f9e7a',
    alt: 'Front Desk, the intake helper: an ivory and gold figure with a signal-arc halo and a jade gem',
    department: 'front-desk',
    art: '/team/v4/desk.webp',
    status: 'In setup',
    reportsTo: 'Echo',
  },
  {
    id: 'booker',
    name: 'Booker',
    role: 'The scheduler',
    job: 'Turns interest into bookings: prepares booking suggestions, reschedules and no-show follow-ups for your OK. Never claims a booking without your calendar confirming it.',
    handles: ['Booking suggestions', 'Reschedule and no-show follow-ups', 'Quiet-lead nudges'],
    accent: '#e0a030',
    alt: 'Booker, the scheduler: an ivory and gold figure with a calendar-ring halo and an amber gem',
    department: 'front-desk',
    art: '/team/v4/booker.webp',
    status: 'In setup',
    reportsTo: 'Echo',
  },
  {
    id: 'keeper',
    name: 'Keeper',
    role: 'The record keeper',
    job: 'Keeps your CRM clean: flags duplicates, fills missing details and finds the leads nobody has followed up. It never writes to a customer.',
    handles: ['Duplicate sweep', 'Missing-info cleanup', 'Stale-lead sweep'],
    accent: '#b5651d',
    alt: 'Keeper, the record keeper: an ivory and gold figure with a ledger-ring halo and a bronze gem',
    department: 'digital-ops',
    art: '/team/v4/keeper.webp',
    status: 'In setup',
    reportsTo: 'Arceus',
  },
];

export const TEAM_BY_ID: Readonly<Record<string, TeamMember>> = Object.fromEntries(
  [TEAM_LEAD, ...TEAM_SPECIALISTS, ...TEAM_CRM].map((m) => [m.id, m])
);

export interface TeamDepartment {
  id: DepartmentId;
  name: string;
  /** Who runs it, in one line. */
  lead: string;
  /** What you get, under 40 words. Worded as work JK sets up and runs, never as a connected feature. */
  body: string;
  /** Concrete examples, shown as a short list. */
  examples: readonly string[];
  members: readonly string[];
}

export const TEAM_DEPARTMENTS: readonly TeamDepartment[] = [
  {
    id: 'front-desk',
    name: 'Front Desk',
    lead: 'Echo answers, with the CRM desk behind it.',
    body: 'Every caller gets an answer and every lead lands in one CRM with its source. Replies, follow-ups and bookings are drafted for your OK, and urgent callers go to a person.',
    examples: ['Missed-call recovery', 'One CRM for web forms, calls, imports and DMs', 'Reply, follow-up and booking drafts'],
    members: ['echo', 'desk', 'booker'],
  },
  {
    id: 'marketing',
    name: 'Marketing',
    lead: 'Lens, Director, Scout and Forge, with Arceus planning the week.',
    body: 'Ads and content that bring in customers, not clicks. SEO and listing audits, research, creative drafts and site fixes land in front of you first. Nothing posts or spends without your OK.',
    examples: ['SEO: website and Google listing audits, fixes drafted for you', 'Posts, scripts and ad copy', 'Site and tracking fixes'],
    members: ['lens', 'director', 'scout', 'forge'],
  },
  {
    id: 'digital-ops',
    name: 'Digital Operations',
    lead: 'Arceus runs the week; JK builds the workflows.',
    body: 'The behind-the-scenes work, built around your tools: a clean CRM, custom workflows for the niche jobs only your business has, every step logged, and a weekly summary.',
    examples: ['Bank-pool reconciliation against your ledger', 'Reading a shared inbox and routing it', 'CRM hygiene, record and inventory syncs'],
    members: ['arceus', 'keeper'],
  },
];

export const TEAM_LEDE = 'One coordinator, four marketing specialists, Echo at the Front Desk, and a three-helper CRM desk in setup. Each has a clear job. You approve anything that spends, posts or texts.';

export const TEAM_STATUS_NOTE = 'Early access: Arceus runs the week today. Lens, Scout and Echo are in training with a JK person reviewing the output; Director and Forge are waiting on access to your ad accounts and Google Business Profile, so our team does that part by hand with your OK. The CRM desk (Front Desk, Booker, Keeper) runs in JK internal trials: every draft is reviewed by a person and nothing is sent to a customer.';

/* ---- Everything we do, in one place ---- */

export interface Capability {
  id: string;
  name: string;
  /** One truthful line, under 30 words. */
  line: string;
  /** Where the detail lives. */
  href: string;
  /** Short status word; omit when live. */
  status?: string;
}

export const CAPABILITIES: readonly Capability[] = [
  {
    id: 'ads',
    name: 'Ads and creative',
    line: 'Software ads, generated spots and creator content, made in house. Campaign plans and copy drafted for your OK.',
    href: '/marketing',
  },
  {
    id: 'front-desk',
    name: 'AI receptionist',
    line: 'Answers calls on your number, logs every caller as a lead and escalates the urgent ones to a person. Text-back waits on carrier approval.',
    href: '/team#team-dept-front-desk',
  },
  {
    id: 'crm',
    name: 'CRM and follow-up',
    line: 'Every lead in one list with its source: web forms, calls, imports, DMs. First replies and follow-ups drafted for your OK. Nothing sends on its own.',
    href: '/team#team-dept-front-desk',
  },
  {
    id: 'seo',
    name: 'SEO and Google listing',
    line: 'An audit of your website and Google listing, with the fixes drafted for you to publish. No ranking promises.',
    href: '/team#team-dept-marketing',
  },
  {
    id: 'research',
    name: 'Market research',
    line: 'Source-linked facts about your customers, market and competitors, with uncertainty labelled.',
    href: '/team#team-dept-marketing',
  },
  {
    id: 'site-fixes',
    name: 'Website and tracking fixes',
    line: 'What to fix on your site, analytics and listing, prepared as changes you review first.',
    href: '/team#team-dept-marketing',
  },
  {
    id: 'report',
    name: 'Weekly report',
    line: 'Every Friday: what we spent, what came in, what it cost per lead, and what we suggest next.',
    href: '/#arceus',
  },
  {
    id: 'workflows',
    name: 'Custom workflows',
    line: 'Missed-call rescue, review requests, no-show rescue, estimate follow-up, pipeline hygiene, and the niche jobs only your business has.',
    href: '/team#team-dept-digital-ops',
  },
  {
    id: 'software',
    name: 'Custom software and apps',
    line: 'Web and mobile builds that fit how your team works: IronLoad, Peppy, TrickBracket and client systems.',
    href: '/work',
  },
  {
    id: 'video',
    name: 'Video pipeline',
    line: 'Fight, driving and product scenes rendered on our own hardware with LTX-2.5, Blender and generative tools.',
    href: '/marketing#fight',
  },
  {
    id: 'ai-employees',
    name: 'AI employees and smart devices',
    line: 'A dedicated AI colleague for one role, or cameras and sensors wired into the software. Prototype stage.',
    href: '/#built',
    status: 'Prototype',
  },
];

/** Superseded by TEAM_STATUS_NOTE; kept unchanged until PR #17, which appends below, lands. */
export const TEAM_NOTE = 'Early access: some teammates are already working and others are still in training. Right now Lens and Director are working with a JK person reviewing the output, and Launch, Echo and Forge are waiting on access to ad accounts, business texting and site publishing, so our team does that part by hand with your OK.';
